import _ from 'lodash';
import { createSelector } from 'reselect';

import {
  getIsFromInternalCompany,
  selectMasterCompanyId,
} from 'store/auth/selectors/master.selectors';

import { getSystem } from './system.selectors';

const ROOT_PARENT_KEY = '__root__';

const TREE_SORT_FIELDS = ['ordinalPosition', 'sectionNumber', 'name'];
const TREE_SORT_ORDERS = ['asc', 'asc', 'asc'];

const buildSectionTree = sections => {
  if (!sections?.length) {
    return [];
  }

  const sortedChildrenByParent = _.mapValues(
    _.groupBy(sections, section => section.parentSectionId ?? ROOT_PARENT_KEY),
    list => _.orderBy(list, TREE_SORT_FIELDS, TREE_SORT_ORDERS),
  );

  const buildNode = section => ({
    ...section,
    children: (sortedChildrenByParent[section.id] ?? []).map(buildNode),
  });

  return (sortedChildrenByParent[ROOT_PARENT_KEY] ?? []).map(buildNode);
};

const selectGuidelinesState = createSelector(getSystem, ({ guidelines }) => guidelines);

export const selectGuidelineSources = createSelector(
  selectGuidelinesState,
  ({ sources }) => sources,
);

export const selectGuidelineSourceById = _.memoize(id =>
  createSelector(selectGuidelineSources, sources => sources.find(source => source.id === id)),
);

export const selectIsGuidelineSourceReadOnly = _.memoize(id =>
  createSelector(
    [selectGuidelineSourceById(id), selectMasterCompanyId, getIsFromInternalCompany],
    (source, masterCompanyId, isInternalCompany) => {
      if (!source || isInternalCompany) {
        return false;
      }

      return source.companyId !== masterCompanyId;
    },
  ),
);

export const selectBusyGuidelineSourceNames = _.memoize(id =>
  createSelector(selectGuidelineSources, sources =>
    sources.reduce((acc, source) => {
      if (source.id !== id) {
        acc.push(source.name);
      }

      return acc;
    }, []),
  ),
);

const selectVersionsState = createSelector(selectGuidelinesState, ({ versions }) => versions);

const selectSectionsState = createSelector(selectGuidelinesState, ({ sections }) => sections);

const selectChunksState = createSelector(selectGuidelinesState, ({ chunks }) => chunks);

export const selectVersionsBySourceId = _.memoize(sourceId =>
  createSelector(selectVersionsState, versions => versions.bySourceId[sourceId] ?? []),
);

export const selectSectionTreeByVersionId = _.memoize(versionId =>
  createSelector(selectSectionsState, sections =>
    buildSectionTree(sections.tocByVersionId[versionId]),
  ),
);

export const selectSectionById = _.memoize(sectionId =>
  createSelector(selectSectionsState, sections => sections.byId[sectionId] ?? null),
);

export const selectChunksBySectionId = _.memoize(sectionId =>
  createSelector(selectChunksState, chunks => chunks.bySectionId[sectionId] ?? []),
);
